import { useState, useCallback, useEffect } from 'react';
import api from '../../../services/api';

/**
 * Hemläxebilagor kan komma som en enskild File (äldre anropare), en array eller
 * en FileList från en input med `multiple`. Alla tre ska gå samma väg, och
 * null/undefined ska ge en tom lista i stället för att smyga in i FormData.
 */
const normaliseraFiler = (filer) => {
  if (!filer) return [];
  if (filer instanceof File) return [filer];
  return Array.from(filer).filter(Boolean);
};

/**
 * Hook för att hantera möten för ett projekt
 */
export function useMeetings(projectId) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hämta möten
  const fetchMeetings = useCallback(async () => {
    if (!projectId) {
      setMeetings([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/meetings/project/${projectId}`);
      setMeetings(response.data.meetings || []);
    } catch (err) {
      console.error('Fel vid hämtning av möten:', err);
      setError(err.response?.data?.message || 'Kunde inte hämta möten');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Hämta vid mount och när projectId ändras
  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Skapa nytt möte
  const createMeeting = useCallback(async (meetingData) => {
    try {
      const response = await api.post('/meetings', {
        projectId,
        ...meetingData
      });
      setMeetings(prev => [...prev, response.data.meeting]);
      return response.data;
    } catch (err) {
      console.error('Fel vid skapande av möte:', err);
      throw err;
    }
  }, [projectId]);

  // Uppdatera möte
  const updateMeeting = useCallback(async (meetingId, updateData) => {
    try {
      const response = await api.put(`/meetings/${meetingId}`, updateData);
      setMeetings(prev =>
        prev.map(m => m._id === meetingId ? response.data.meeting : m)
      );
      return response.data;
    } catch (err) {
      console.error('Fel vid uppdatering av möte:', err);
      throw err;
    }
  }, []);

  // Ta bort möte
  const deleteMeeting = useCallback(async (meetingId) => {
    try {
      await api.delete(`/meetings/${meetingId}`);
      setMeetings(prev => prev.filter(m => m._id !== meetingId));
      return { success: true };
    } catch (err) {
      console.error('Fel vid borttagning av möte:', err);
      throw err;
    }
  }, []);

  // Skicka tidsförslag (stödjer valfria hemläxa-filer)
  const proposeTimesForMeeting = useCallback(async (meetingId, proposals, message, homeworkFiles = null) => {
    try {
      const filer = normaliseraFiler(homeworkFiles);
      let response;
      if (filer.length > 0) {
        // Skicka som FormData med bifogade filer. Samma fältnamn för alla —
        // backend läser dem med multer.array och bifogar varje fil i mailet.
        const fd = new FormData();
        fd.append('proposals', JSON.stringify(proposals));
        fd.append('message', message || '');
        filer.forEach(fil => fd.append('homeworkFile', fil));
        response = await api.post(`/meetings/${meetingId}/propose`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await api.post(`/meetings/${meetingId}/propose`, {
          proposals,
          message
        });
      }
      await fetchMeetings();
      return response.data;
    } catch (err) {
      console.error('Fel vid skickande av tidsförslag:', err);
      throw err;
    }
  }, [fetchMeetings]);

  // Skapa möte och skicka tidsförslag i ett steg (stödjer valfria hemläxa-filer)
  const createAndPropose = useCallback(async (meetingData, proposals, message, homeworkFiles = null) => {
    try {
      // 1. Skapa mötet
      const createResponse = await api.post('/meetings', {
        projectId,
        ...meetingData
      });
      const newMeeting = createResponse.data.meeting;

      // 2. Skicka tidsförslag (med eventuella hemläxa-filer)
      const filer = normaliseraFiler(homeworkFiles);
      let proposeResponse;
      if (filer.length > 0) {
        const fd = new FormData();
        fd.append('proposals', JSON.stringify(proposals));
        fd.append('message', message || '');
        filer.forEach(fil => fd.append('homeworkFile', fil));
        proposeResponse = await api.post(`/meetings/${newMeeting._id}/propose`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        proposeResponse = await api.post(`/meetings/${newMeeting._id}/propose`, {
          proposals,
          message
        });
      }

      // 3. Uppdatera listan
      await fetchMeetings();

      return proposeResponse.data;
    } catch (err) {
      console.error('Fel vid skapande och skickande av tidsförslag:', err);
      throw err;
    }
  }, [projectId, fetchMeetings]);

  // Boka möte (direktbokning eller välj förslag)
  // Stöder även återkommande möten som returnerar en array
  const bookMeeting = useCallback(async (meetingId, bookingData) => {
    try {
      const response = await api.post(`/meetings/${meetingId}/book`, bookingData);

      // Hantera återkommande möten (returnerar meetings array)
      if (response.data.meetings) {
        await fetchMeetings(); // Uppdatera hela listan för återkommande
      } else {
        setMeetings(prev =>
          prev.map(m => m._id === meetingId ? response.data.meeting : m)
        );
      }
      return response.data;
    } catch (err) {
      console.error('Fel vid bokning av möte:', err);
      throw err;
    }
  }, [fetchMeetings]);

  // Skapa möte och boka direkt i ett steg
  // Stöder även återkommande möten
  const createAndBook = useCallback(async (meetingData, bookingData) => {
    try {
      // 1. Skapa mötet
      const createResponse = await api.post('/meetings', {
        projectId,
        ...meetingData
      });
      const newMeeting = createResponse.data.meeting;

      // 2. Boka mötet direkt (kan vara återkommande)
      const bookResponse = await api.post(`/meetings/${newMeeting._id}/book`, bookingData);

      // 3. Uppdatera listan (alltid för att hantera både enstaka och återkommande)
      await fetchMeetings();

      return bookResponse.data;
    } catch (err) {
      console.error('Fel vid skapande och bokning av möte:', err);
      throw err;
    }
  }, [projectId, fetchMeetings]);

  // Boka om möte till ny tid
  const rescheduleMeeting = useCallback(async (meetingId, { date, time }) => {
    try {
      const response = await api.post(`/meetings/${meetingId}/reschedule`, { date, time });
      setMeetings(prev =>
        prev.map(m => m._id === meetingId ? response.data.meeting : m)
      );
      return response.data;
    } catch (err) {
      console.error('Fel vid ombokning av möte:', err);
      throw err;
    }
  }, []);

  // Markera som genomfört
  const completeMeeting = useCallback(async (meetingId) => {
    try {
      const response = await api.post(`/meetings/${meetingId}/complete`);
      setMeetings(prev =>
        prev.map(m => m._id === meetingId ? { ...m, status: 'completed' } : m)
      );
      return response.data;
    } catch (err) {
      console.error('Fel vid markering av möte som genomfört:', err);
      throw err;
    }
  }, []);

  // Avboka möte
  const cancelMeeting = useCallback(async (meetingId, reason) => {
    try {
      const response = await api.post(`/meetings/${meetingId}/cancel`, { reason });
      setMeetings(prev =>
        prev.map(m => m._id === meetingId ? { ...m, status: 'cancelled' } : m)
      );
      return response.data;
    } catch (err) {
      console.error('Fel vid avbokning av möte:', err);
      throw err;
    }
  }, []);

  // Hämta svarsstatus för ett möte
  const getProposalResponses = useCallback(async (meetingId) => {
    try {
      const response = await api.get(`/meetings/${meetingId}/responses`);
      return response.data;
    } catch (err) {
      console.error('Fel vid hämtning av svarsstatus:', err);
      throw err;
    }
  }, []);

  return {
    meetings,
    loading,
    error,
    fetchMeetings,
    createMeeting,
    createAndPropose,
    createAndBook,
    updateMeeting,
    deleteMeeting,
    proposeTimesForMeeting,
    bookMeeting,
    rescheduleMeeting,
    completeMeeting,
    cancelMeeting,
    getProposalResponses
  };
}

/**
 * Hook för att hämta nästa möte för ett projekt
 */
export function useNextMeeting(projectId) {
  const [nextMeeting, setNextMeeting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setNextMeeting(null);
      setLoading(false);
      return;
    }

    const fetchNextMeeting = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/meetings/project/${projectId}/next`);
        setNextMeeting(response.data.meeting);
      } catch (err) {
        console.error('Fel vid hämtning av nästa möte:', err);
        setNextMeeting(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNextMeeting();
  }, [projectId]);

  return { nextMeeting, loading };
}

/**
 * Hook för att hämta möteskonfiguration
 */
export function useMeetingConfig() {
  const [config, setConfig] = useState({ meetingTypes: [], officeAddress: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/meetings/config');
        setConfig({
          meetingTypes: response.data.meetingTypes || [],
          officeAddress: response.data.officeAddress || ''
        });
      } catch (err) {
        console.error('Fel vid hämtning av möteskonfiguration:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return { config, loading };
}

/**
 * Hook för att hämta möten för en kund (alla projekt)
 */
export function useCustomerMeetings(customerId) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMeetings = useCallback(async () => {
    if (!customerId) {
      setMeetings([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/meetings/customer/${customerId}`);
      setMeetings(response.data.meetings || []);
    } catch (err) {
      console.error('Fel vid hämtning av kundmöten:', err);
      setError(err.response?.data?.message || 'Kunde inte hämta möten');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  return { meetings, loading, error, refetch: fetchMeetings };
}

export default useMeetings;
